import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Send, Sparkles, PlusCircle, ThumbsUp } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { usePageSeo } from '../hooks/usePageSeo';
import { fetchSoftwareOptions } from '../utils/fetchSoftwareOptions';
import { insertForumSuggestion } from '../utils/forumSuggestionsDb';
import {
  findDuplicateSiteUrl,
  formatDuplicateSiteUrlMessage,
} from '../utils/forumUrlDuplicate';
import { absoluteUrl } from '../utils/seo';
import { formatSiteAddedDate } from '../utils/formatLastUpdated';
import SuggestedForumsPanel from './SuggestedForumsPanel';
import { useLanguage } from '../contexts/LanguageContext';

interface Props {
  darkMode: boolean;
}

type TabId = 'new' | 'pending' | 'suggest';

function parseTab(raw: string | null): TabId {
  if (raw === 'pending' || raw === 'suggest' || raw === 'new') return raw;
  return 'new';
}

interface Category {
  id: string;
  name: string;
}

type NewForumRow = {
  id: string;
  forum_id: string;
  name: string;
  category: string;
  created_at: string;
  members_count: number;
};

type FormData = {
  name: string;
  url: string;
  categoryId: string;
  software: string;
  submitterName: string;
  description: string;
};

const CommunityForumsPage: React.FC<Props> = ({ darkMode }) => {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseTab(searchParams.get('tab'));

  const [forums, setForums] = useState<NewForumRow[]>([]);
  const [forumsLoading, setForumsLoading] = useState(true);
  const [categories, setCategories] = useState<Category[]>([]);
  const [softwareOptions, setSoftwareOptions] = useState<string[]>([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [suggestionsRefreshKey, setSuggestionsRefreshKey] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    url: '',
    categoryId: '',
    software: '',
    submitterName: '',
    description: '',
  });

  const setTab = useCallback(
    (tab: TabId) => {
      if (tab === 'new') {
        setSearchParams({}, { replace: true });
      } else {
        setSearchParams({ tab }, { replace: true });
      }
    },
    [setSearchParams]
  );

  usePageSeo(
    activeTab === 'new'
      ? {
          title: 'Yeni eklenen forumlar',
          description:
            'Siteye son eklenen forumlar, bekleyen topluluk önerileri ve yeni forum önerisi gönderme.',
          keywords: 'yeni forumlar, forum öner, forum destek',
          canonical: absoluteUrl('/new-forums'),
        }
      : activeTab === 'pending'
        ? {
            title: 'Bekleyen forum önerileri',
            description: 'Onay bekleyen forum önerilerini destekleyin.',
            noindex: true,
          }
        : {
            title: 'Forum öner',
            description: 'Listede olmayan bir forum sitesi önerin.',
            keywords: 'forum öner, forum ekle',
            noindex: true,
          },
    [activeTab]
  );

  useEffect(() => {
    if (activeTab !== 'new') return;
    setForumsLoading(true);
    supabase
      .from('forums')
      .select('id, forum_id, name, category, created_at, members_count')
      .eq('is_active', true)
      .not('created_at', 'is', null)
      .order('created_at', { ascending: false })
      .limit(30)
      .then(({ data }) => {
        setForums(data || []);
        setForumsLoading(false);
      });
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'suggest') return;
    fetchCategories();
    fetchSoftwareList();
  }, [activeTab]);

  const fetchSoftwareList = async () => {
    try {
      const names = await fetchSoftwareOptions();
      setSoftwareOptions(names);
    } catch (error) {
      console.error('Error fetching software:', error);
      toast.error('Forum yazılımları yüklenemedi');
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase.from('categories').select('id, name').order('name');
      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Kategoriler yüklenirken bir hata oluştu');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      const duplicate = await findDuplicateSiteUrl(formData.url);
      if (duplicate) {
        toast.error(formatDuplicateSiteUrlMessage(duplicate), { duration: 6000 });
        return;
      }

      const row: Record<string, unknown> = {
        name: formData.name,
        url: formData.url,
        category_id: formData.categoryId,
        status: 'pending',
        description: `Öneren: ${formData.submitterName}\n\n${formData.description}`,
      };
      if (formData.software.trim()) {
        row.software = formData.software.trim();
      }
      const { error } = await insertForumSuggestion(row);
      if (error) throw error;
      toast.success('Forum öneriniz başarıyla gönderildi');
      setSuggestionsRefreshKey((key) => key + 1);
      setFormData({
        name: '',
        url: '',
        categoryId: '',
        software: '',
        submitterName: '',
        description: '',
      });
      setTab('pending');
    } catch (error) {
      console.error('Error submitting forum:', error);
      toast.error('Forum önerisi gönderilirken bir hata oluştu');
    } finally {
      setSubmitLoading(false);
    }
  };

  const inputClass = `w-full px-4 py-2 rounded-lg border ${
    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
  }`;
  const cardClass = darkMode ? 'bg-gray-800' : 'bg-white shadow-sm';
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const text = darkMode ? 'text-white' : 'text-gray-900';

  const tabs: { id: TabId; label: string; icon: React.ComponentType<{ size?: number; className?: string }> }[] = [
    { id: 'new', label: t('communityTabNew'), icon: Sparkles },
    { id: 'pending', label: t('communityTabPending'), icon: ThumbsUp },
    { id: 'suggest', label: t('communityTabSuggest'), icon: PlusCircle },
  ];

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Link
          to="/"
          className={`inline-flex items-center gap-2 mb-6 ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
        >
          <ArrowLeft size={20} />
          {t('communityBack')}
        </Link>

        <h1 className={`text-2xl font-bold mb-2 flex items-center gap-2 ${text}`}>
          <Sparkles className="text-amber-500 shrink-0" />
          {t('communityPageTitle')}
        </h1>
        <p className={`text-sm mb-6 ${muted}`}>{t('communityPageIntro')}</p>

        <div
          className={`flex flex-wrap gap-2 mb-8 p-1 rounded-xl border ${
            darkMode ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200 shadow-sm'
          }`}
          role="tablist"
          aria-label={t('communityPageTitle')}
        >
          {tabs.map(({ id, label, icon: Icon }) => {
            const active = activeTab === id;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={active}
                aria-controls={`community-panel-${id}`}
                id={`community-tab-${id}`}
                onClick={() => setTab(id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
                  active
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : darkMode
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon size={18} className={active ? 'text-white' : 'text-indigo-500'} />
                {label}
              </button>
            );
          })}
        </div>

        {activeTab === 'new' && (
          <section
            id="community-panel-new"
            role="tabpanel"
            aria-labelledby="community-tab-new"
          >
            {forumsLoading ? (
              <div className="animate-spin h-10 w-10 border-b-2 border-indigo-500 rounded-full" />
            ) : forums.length === 0 ? (
              <p className={muted}>{t('communityNoNewForums')}</p>
            ) : (
              <ul className="grid gap-3">
                {forums.map((f) => (
                  <li key={f.id}>
                    <Link
                      to={`/forum/${f.forum_id}`}
                      className={`block p-4 rounded-xl ${cardClass}`}
                    >
                      <span className="font-semibold">{f.name}</span>
                      <span className={`block text-sm mt-1 ${muted}`}>
                        {f.category} · {formatSiteAddedDate(f.created_at)} ·{' '}
                        {f.members_count.toLocaleString('tr-TR')} üye
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {activeTab === 'pending' && (
          <section
            id="community-panel-pending"
            role="tabpanel"
            aria-labelledby="community-tab-pending"
          >
            <SuggestedForumsPanel darkMode={darkMode} refreshKey={suggestionsRefreshKey} />
          </section>
        )}

        {activeTab === 'suggest' && (
          <section
            id="community-panel-suggest"
            role="tabpanel"
            aria-labelledby="community-tab-suggest"
            className={`rounded-xl p-6 ${cardClass}`}
          >
            <h2 className={`text-xl font-semibold mb-6 ${text}`}>{t('communityTabSuggest')}</h2>
            <form onSubmit={handleSubmit} className="space-y-6 max-w-xl">
              <div>
                <label className="block text-sm font-medium mb-2">Forum Adı</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Forum URL</label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Kategori</label>
                <select
                  value={formData.categoryId}
                  onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                  className={inputClass}
                  required
                >
                  <option value="">Kategori Seçin</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Forum yazılımı (isteğe bağlı)</label>
                <select
                  value={formData.software}
                  onChange={(e) => setFormData({ ...formData, software: e.target.value })}
                  disabled={softwareOptions.length === 0}
                  className={`${inputClass} disabled:opacity-50`}
                >
                  <option value="">Yazılım seçin</option>
                  {softwareOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                {softwareOptions.length === 0 && (
                  <p className={`mt-1 text-xs ${muted}`}>
                    Yazılım listesi yükleniyor veya henüz tanımlı değil.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Adınız</label>
                <input
                  type="text"
                  value={formData.submitterName}
                  onChange={(e) => setFormData({ ...formData, submitterName: e.target.value })}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Açıklama</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className={inputClass}
                  placeholder="Forum hakkında ek bilgiler..."
                />
              </div>
              <button
                type="submit"
                disabled={submitLoading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                <Send size={18} />
                {submitLoading ? 'Gönderiliyor...' : 'Gönder'}
              </button>
            </form>
          </section>
        )}
      </div>
    </div>
  );
};

export default CommunityForumsPage;
